/**
 * Batch Traceability (/batch-traceability) — the batch's audit trail (Execution revamp step 5, EXV-050 … EXV-056).
 *
 * Three cards, every fact as the server sent it (`GET /bp/batch_full_audit_trail/journal`):
 *   1. the batch: number, record and product, the server's status word and QA disposition (who, when), the gate facts
 *      and the hash chain's verification (walked on the server, with when it was checked);
 *   2. its trail in sections — Events (the chained journal: verb, who, when, record, reason, meaning, chain position,
 *      hash; values and old → new as labelled fields), Signatures, Operations, Deviations, Lots;
 *   3. the master record's own lifecycle, under its name and code — never as batch facts.
 * The client decides nothing: no status, count or word is derived here. The picker is debounced and ignores a stale
 * answer; a refusal, a not-found and a failure are each shown as what they are.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, Empty, Select, Skeleton, Tabs, Tag, theme } from 'antd';
import { GitBranch, MoreVertical } from 'lucide-react';
import DataTable from '@/components/DataTable';
import PageHeader from '@/components/PageHeader';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import StatusIndicator, { StatusNotice } from '@/components/genealogy/StatusIndicator';
import BatchStatusBanner from '@/components/batch/BatchStatusBanner';
import { DispositionIndicator } from '@/components/batch/BatchStatusCell';
import { PageInfo, RefusalState } from '@/components/PageChrome';
import { get_batch_list_360, get_batch_audit_trail_journal } from '@/utils/api';
import { formatDateTime } from '@/utils/datetime';
import { SEARCH_DEBOUNCE_DELAY } from '@/constants/recipe/recipe.constants';
import './traceability/BatchTraceability.css';

const fmt = (v) => (v ? formatDateTime(v) : '—');
const dash = (v) => (v === null || v === undefined || v === '' ? '—' : v);
const ABOUT =
  'Who did what, when and why on one batch: its chained event journal with the chain verified on the server, every ' +
  'signature on the record, operations attributed from the journal, deviations and recorded lots. The master record’s ' +
  'own history is kept apart.';

export function useTraceVars() {
  const { token } = theme.useToken();
  return useMemo(
    () => ({
      '--btr-card': token.colorBgContainer,
      '--btr-border': token.colorBorderSecondary,
      '--btr-text': token.colorText,
      '--btr-muted': token.colorTextSecondary,
    }),
    [token]
  );
}

// ---------------------------------------------------------------- columns, one set per section
const eventColumns = [
  {
    title: '#',
    dataIndex: 'seq',
    key: 'seq',
    width: 96,
    render: (v, r) => (r.chained === false ? <span data-testid="not-chained">Not chained</span> : dash(v)),
  },
  { title: 'Event', dataIndex: 'verb', key: 'verb', render: dash },
  { title: 'By', dataIndex: 'who', key: 'who', render: dash },
  { title: 'When', dataIndex: 'when', key: 'when', render: fmt },
  { title: 'Record', dataIndex: 'record', key: 'record', render: dash },
  { title: 'Reason', dataIndex: 'reason', key: 'reason', render: dash },
  { title: 'Meaning', dataIndex: 'meanings', key: 'meanings', render: (m) => (m && m.length ? m.join(', ') : '—') },
];

const signatureColumns = [
  { title: 'Signed by', dataIndex: 'name', key: 'name', render: dash },
  { title: 'Role', dataIndex: 'role', key: 'role', render: dash },
  { title: 'Meaning', dataIndex: 'meaning', key: 'meaning', render: dash },
  { title: 'When', dataIndex: 'signed_at', key: 'signed_at', render: fmt },
  { title: 'Signed on', dataIndex: 'document', key: 'document', render: dash },
  { title: 'Reason', dataIndex: 'reason', key: 'reason', render: dash },
  { title: 'Standing', dataIndex: 'validity', key: 'validity', render: dash },
];

const operationColumns = [
  { title: '#', dataIndex: 'operation_number', key: 'operation_number', width: 56, render: dash },
  { title: 'Operation', dataIndex: 'operation_name', key: 'operation_name', render: dash },
  { title: 'Process', dataIndex: 'process_name', key: 'process_name', render: dash },
  { title: 'Status', dataIndex: 'status', key: 'status', render: dash },
  { title: 'Started', dataIndex: 'started_at', key: 'started_at', render: fmt },
  { title: 'Started by', dataIndex: 'started_by', key: 'started_by', render: dash },
  { title: 'Ended', dataIndex: 'ended_at', key: 'ended_at', render: fmt },
  { title: 'Ended by', dataIndex: 'ended_by', key: 'ended_by', render: dash },
];

const deviationColumns = [
  { title: 'Operation', dataIndex: 'operation_name', key: 'operation_name', render: dash },
  { title: 'Type', dataIndex: 'type', key: 'type', render: dash },
  { title: 'Parameter', dataIndex: 'parameter', key: 'parameter', render: dash },
  { title: 'Actual', dataIndex: 'actual', key: 'actual', render: dash },
  { title: 'Limit', dataIndex: 'limit', key: 'limit', render: dash },
  { title: 'Status', dataIndex: 'status', key: 'status', render: dash },
  { title: 'Raised by', dataIndex: 'raised_by', key: 'raised_by', render: dash },
  { title: 'When', dataIndex: 'when', key: 'when', render: dash },
];

const lotColumns = [
  { title: 'Lot', dataIndex: 'lot', key: 'lot', render: dash },
  { title: 'Material', dataIndex: 'material', key: 'material', render: dash },
  { title: 'Material code', dataIndex: 'material_code', key: 'material_code', render: dash },
  { title: 'Container', dataIndex: 'container', key: 'container', render: dash },
  { title: 'Quantity', key: 'quantity', render: (_, r) => (r.quantity ? `${r.quantity}${r.unit ? ` ${r.unit}` : ''}` : '—') },
  { title: 'Action', dataIndex: 'action', key: 'action', render: dash },
  { title: 'By', dataIndex: 'who', key: 'who', render: dash },
  { title: 'When', dataIndex: 'when', key: 'when', render: fmt },
];

const holdColumns = [
  { title: 'Action', dataIndex: 'action', key: 'action', render: dash },
  { title: 'Operation', dataIndex: 'operation', key: 'operation', render: dash },
  { title: 'Started', dataIndex: 'started_at', key: 'started_at', render: fmt },
  { title: 'Started by', dataIndex: 'started_by', key: 'started_by', render: dash },
  { title: 'Ended', dataIndex: 'ended_at', key: 'ended_at', render: fmt },
  { title: 'Ended by', dataIndex: 'ended_by', key: 'ended_by', render: dash },
  { title: 'Reason', dataIndex: 'reason', key: 'reason', render: dash },
  { title: 'Comment', dataIndex: 'comment', key: 'comment', render: dash },
];

/** A changed value as the server sent it: its text, or its labelled fields (never raw JSON). */
const ChangeValue = ({ value }) =>
  value && value.fields && value.fields.length ? (
    <dl className="btr-detail">
      {value.fields.map((f, i) => (
        <React.Fragment key={i}>
          <dt>{f.label || 'Value'}</dt>
          <dd>{dash(f.value)}</dd>
        </React.Fragment>
      ))}
    </dl>
  ) : (
    dash(value && value.text)
  );

const changeColumns = [
  { title: 'Record', dataIndex: 'record', key: 'record', render: dash },
  { title: 'Field', dataIndex: 'field', key: 'field', render: dash },
  { title: 'Old', dataIndex: 'old', key: 'old', render: (v) => <ChangeValue value={v} /> },
  { title: 'New', dataIndex: 'new', key: 'new', render: (v) => <ChangeValue value={v} /> },
  { title: 'By', dataIndex: 'who', key: 'who', render: dash },
  { title: 'When', dataIndex: 'when', key: 'when', render: fmt },
  { title: 'Reason', dataIndex: 'reason', key: 'reason', render: dash },
];

const handoverColumns = [
  { title: 'Employee', dataIndex: 'employee', key: 'employee', render: dash },
  { title: 'Process', dataIndex: 'process', key: 'process', render: dash },
  { title: 'Available', dataIndex: 'available', key: 'available', render: (v) => (v ? 'Yes' : 'No') },
  { title: 'Comment', dataIndex: 'comment', key: 'comment', render: dash },
  { title: 'When', dataIndex: 'when', key: 'when', render: fmt },
];

const machineColumns = [
  { title: 'Machine', dataIndex: 'machine', key: 'machine', render: dash },
  { title: 'Action', dataIndex: 'action', key: 'action', render: dash },
  { title: 'Status', dataIndex: 'status', key: 'status', render: dash },
  { title: 'Operation #', dataIndex: 'operation_number', key: 'operation_number', render: dash },
  { title: 'By', dataIndex: 'who', key: 'who', render: dash },
  { title: 'When', dataIndex: 'when', key: 'when', render: fmt },
];

const cleaningColumns = [
  { title: 'Machine', dataIndex: 'machine', key: 'machine', render: dash },
  { title: 'Cleaning type', dataIndex: 'cleaning_type', key: 'cleaning_type', render: dash },
  { title: 'Status', dataIndex: 'status', key: 'status', render: dash },
  { title: 'Started by', dataIndex: 'started_by', key: 'started_by', render: dash },
  { title: 'Started', dataIndex: 'started_at', key: 'started_at', render: fmt },
  { title: 'Completed by', dataIndex: 'completed_by', key: 'completed_by', render: dash },
  { title: 'Completed', dataIndex: 'completed_at', key: 'completed_at', render: fmt },
];

const masterColumns = [
  { title: 'Event', dataIndex: 'verb', key: 'verb', render: dash },
  { title: 'By', dataIndex: 'who', key: 'who', render: dash },
  { title: 'When', dataIndex: 'when', key: 'when', render: fmt },
  { title: 'Detail', dataIndex: 'detail', key: 'detail', render: dash },
];

/** An event's recorded values, old → new changes and hashes, as labelled fields (never the raw payload). */
function EventDetail({ event }) {
  return (
    <dl className="btr-detail" data-testid="event-detail">
      {(event.values || []).map((v, i) => (
        <React.Fragment key={`v${i}`}>
          <dt>{v.label || 'Value'}</dt>
          <dd>
            {v.value}
            {v.unit ? ` ${v.unit}` : ''}
            {v.verdict ? ` · ${v.verdict}` : ''}
          </dd>
        </React.Fragment>
      ))}
      {(event.changes || []).map((c, i) => (
        <React.Fragment key={`c${i}`}>
          <dt>{c.label || 'Changed'}</dt>
          <dd>
            {dash(c.old)} → {dash(c.new)}
          </dd>
        </React.Fragment>
      ))}
      {event.seq != null ? (
        <>
          <dt>Chain position</dt>
          <dd>{event.seq}</dd>
          <dt>Record hash</dt>
          <dd className="btr-mono">{dash(event.record_hash)}</dd>
          <dt>Previous hash</dt>
          <dd className="btr-mono">{dash(event.previous_hash)}</dd>
        </>
      ) : null}
    </dl>
  );
}

const table = (columns, rows, testId, extra = {}) => (
  <div data-testid={testId} style={{ minWidth: 0 }}>
    <DataTable
      tableKey={testId}
      searchable
      columnConfig
      showCount={false}
      size="small"
      rowKey={(r) => (r.seq != null ? `s${r.seq}` : `${testId}-${rows.indexOf(r)}`)}
      columns={columns}
      data={rows}
      scrollX="max-content"
      height={null}
      minHeight={160}
      emptyMessage="None recorded"
      {...extra}
    />
  </div>
);

export function HeroCard({ data, chainOnly = false }) {
  const b = data.batch || {};
  const chain = data.chain;
  if (chainOnly) {
    // opt-in for a page that already shows the batch, its status and its disposition (Review by Exception): only
    // the event chain check, as one line
    return chain ? (
      <div data-testid="chain-state" aria-label="Event chain" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, margin: '4px 0 10px', fontSize: 12.5 }}>
        <StatusIndicator state={chain.verified ? 'ok' : 'error'} label={chain.word} />
        <span data-testid="chain-checked" style={{ opacity: 0.75 }}>
          {chain.length} events · checked {fmt(chain.checked_at)}
          {chain.first_break ? ` · first break at #${chain.first_break.seq}` : ''}
        </span>
      </div>
    ) : null;
  }
  return (
    <section className="btr-card" aria-label="Batch" data-testid="trace-hero">
      <div className="btr-hero">
        <div style={{ minWidth: 0 }}>
          <div className="btr-batch-no">{b.batch_number}</div>
          <div className="btr-sub">{[b.product, b.record_name, b.record_code].filter(Boolean).join(' · ') || '—'}</div>
        </div>
        <div className="btr-facts">
          <div>
            <div className="btr-fact-label">Status</div>
            <div className="btr-fact-value" data-testid="trace-status">
              {b.status_word ? <StatusIndicator state={b.status_tone} label={b.status_word} /> : '—'}
            </div>
          </div>
          <div>
            <div className="btr-fact-label">Disposition</div>
            <div className="btr-fact-value" data-testid="release-pill">
              {b.disposition && b.disposition.word ? <DispositionIndicator disposition={b.disposition} /> : '—'}
            </div>
          </div>
          <div>
            <div className="btr-fact-label">Event chain</div>
            <div className="btr-fact-value" data-testid="chain-state">
              {chain ? <StatusIndicator state={chain.verified ? 'ok' : 'error'} label={chain.word} /> : '—'}
            </div>
            {chain ? (
              <div className="btr-fact-sub" data-testid="chain-checked">
                {chain.length} events · checked {fmt(chain.checked_at)}
                {chain.first_break ? ` · first break at #${chain.first_break.seq}` : ''}
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <BatchStatusBanner gate={b.gate} statusWord={b.status_word} style={{ margin: '0 14px 14px' }} />
    </section>
  );
}

export function TrailCard({ data, batchNumber }) {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [section, setSection] = useState('events');
  const counts = data.counts || {};
  const lots = data.lots || [];
  const machines = data.machines || {};
  const genealogy = (
    <Button
      className="btr-btn"
      icon={<GitBranch size={16} aria-hidden="true" />}
      onClick={() => navigate(`/batch-genealogy-full?batch=${encodeURIComponent(batchNumber)}`)}
    >
      Open Batch Genealogy
    </Button>
  );
  const panes = {
    events: table(eventColumns, data.events || [], 'trace-events', {
      expandable: {
        expandedRowRender: (r) => <EventDetail event={r} />,
        rowExpandable: (r) => r.seq != null || (r.values || []).length > 0 || (r.changes || []).length > 0,
      },
    }),
    signatures: table(signatureColumns, data.signatures || [], 'trace-signatures'),
    operations: table(operationColumns, data.operations || [], 'trace-operations'),
    deviations: table(deviationColumns, data.deviations || [], 'trace-deviations'),
    holds: table(holdColumns, data.holds || [], 'trace-holds'),
    changes: table(changeColumns, data.change_history || [], 'trace-changes'),
    handovers: table(handoverColumns, data.handovers || [], 'trace-handovers'),
    machines: (
      <>
        <h3 className="btr-card-title" style={{ marginBottom: 8 }}>Machine state changes</h3>
        {table(machineColumns, machines.events || [], 'trace-machine-events')}
        <h3 className="btr-card-title" style={{ margin: '12px 0 8px' }}>Cleaning</h3>
        {table(cleaningColumns, machines.cleaning || [], 'trace-cleaning')}
      </>
    ),
    lots: lots.length ? (
      <>
        {table(lotColumns, lots, 'trace-lots')}
        <div style={{ marginTop: 8 }}>{genealogy}</div>
      </>
    ) : (
      <div className="btr-empty" data-testid="trace-no-lots">
        <span>No material lots recorded for this batch</span>
        {genealogy}
      </div>
    ),
  };
  const options = [
    { value: 'events', label: `Events (${counts.events ?? 0})` },
    { value: 'signatures', label: `Signatures (${counts.signatures ?? 0})` },
    { value: 'operations', label: `Operations (${counts.operations ?? 0})` },
    { value: 'deviations', label: `Deviations (${counts.deviations ?? 0})` },
    { value: 'lots', label: `Lots (${lots.length})` },
    { value: 'holds', label: `Holds & pauses (${counts.holds ?? 0})` },
    { value: 'changes', label: `Change history (${counts.change_history ?? 0})` },
    { value: 'handovers', label: `Handovers (${counts.handovers ?? 0})` },
    { value: 'machines', label: `Machines & cleaning (${counts.machines ?? 0})` },
  ];
  return (
    <section aria-label="Audit trail" data-testid="trace-trail">
      <Tabs
        activeKey={section}
        onChange={setSection}
        size="large"
        destroyInactiveTabPane
        more={{ icon: <MoreVertical size={16} /> }}
        tabBarStyle={{ backgroundColor: isDark ? defaultColors.palette.gray[700] : '#f9f9f9', borderRadius: '8px', padding: '2px 8px' }}
        items={options.map((o) => ({ key: o.value, label: o.label, children: panes[o.value] }))}
      />
    </section>
  );
}

export function MasterRecordCard({ master }) {
  if (!master) return null;
  const title = [master.name, master.code].filter(Boolean).join(' · ');
  return (
    <section className="btr-card" aria-label="Master record" data-testid="trace-master">
      <div className="btr-card-head">
        <h2 className="btr-card-title">Master record{title ? ` · ${title}` : ''}</h2>
      </div>
      <div className="btr-card-body">{table(masterColumns, master.events || [], 'trace-master-events')}</div>
    </section>
  );
}

// what the box shows once a batch is picked: one compact line
const optionLabel = (b) => (
  <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
    <strong>{b.batch_number}</strong>
    {b.product ? <span style={{ opacity: 0.7, marginLeft: 8 }}>{b.product}</span> : null}
  </span>
);

// what the list shows: batch number and its status chips on the first line, the product under it
const optionRows = (b) => (
  <div style={{ lineHeight: 1.35, padding: '2px 0' }}>
    <div className="flex items-center gap-2">
      <strong>{b.batch_number}</strong>
      {b.status_word ? <Tag style={{ margin: 0 }}>{b.status_word}</Tag> : null}
      {b.disposition && b.disposition.word ? <Tag color="green" style={{ margin: 0 }}>{b.disposition.word}</Tag> : null}
    </div>
    {b.product ? <div style={{ fontSize: 12, opacity: 0.65 }}>{b.product}</div> : null}
  </div>
);

const Batch360 = () => {
  const vars = useTraceVars();
  const [params, setParams] = useSearchParams();
  const selected = params.get('batch') || null;
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [batches, setBatches] = useState([]);
  const [listRefusal, setListRefusal] = useState(null);
  const [state, setState] = useState({ loading: false, data: null, error: null });
  const listSeq = useRef(0);
  const detailSeq = useRef(0);

  // the picker: a request per settled search (debounced), and only the latest answer is shown
  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), SEARCH_DEBOUNCE_DELAY);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => {
    const mine = ++listSeq.current;
    get_batch_list_360(query, (res) => {
      if (mine !== listSeq.current) return;
      if (res?.data?.status === 'success') setBatches(res.data.data || []);
      setListRefusal(res?.data?.refusal || null);
    });
  }, [query]);

  const load = useCallback(() => {
    const mine = ++detailSeq.current;
    if (!selected) {
      setState({ loading: false, data: null, error: null });
      return;
    }
    setState({ loading: true, data: null, error: null });
    get_batch_audit_trail_journal(selected)
      .then((data) => mine === detailSeq.current && setState({ loading: false, data, error: null }))
      .catch((error) => mine === detailSeq.current && setState({ loading: false, data: null, error }));
  }, [selected]);
  useEffect(load, [load]);

  const options = useMemo(() => {
    const rows = batches.map((b) => ({ value: b.batch_number, label: optionLabel(b), rows: optionRows(b) }));
    if (selected && !rows.some((o) => o.value === selected)) rows.unshift({ value: selected, label: selected });
    return rows;
  }, [batches, selected]);

  const pick = (value) => {
    const next = new URLSearchParams(params);
    if (value) next.set('batch', value);
    else next.delete('batch');
    setParams(next, { replace: true });
  };

  const { loading, data, error } = state;
  const refusal = listRefusal || error?.refusal || null;
  const { isDark } = useTheme();
  return (
    <div className="p-2.5 pt-5" style={{ backgroundColor: isDark ? defaultColors.palette.gray[900] : '#f9fafb' }}>
      <Card
        bordered={false}
        className="border border-gray-300 shadow-md"
        classNames={{ body: 'page-card-scroll' }}
        style={{ backgroundColor: isDark ? defaultColors.palette.gray[800] : '#fff', height: 'calc(100dvh - 120px)', display: 'flex', flexDirection: 'column' }}
        styles={{ body: { flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 16px' } }}
        title={
          <PageHeader
            compact
            icon={<GitBranch size={22} />}
            title="Batch Traceability"
            description="Who did what, when and why on one batch"
            meta={<PageInfo text={ABOUT} />}
            filters={
              <Select
                className="btr-picker"
                popupMatchSelectWidth={false}
                popupClassName="btr-picker-popup"
                optionRender={(opt) => opt.data.rows || opt.label}
                showSearch
                allowClear
                aria-label="Batch"
                placeholder="Select a batch"
                value={selected}
                onChange={pick}
                onSearch={setSearch}
                filterOption={false}
                options={options}
                notFoundContent={<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No batches" />}
                data-testid="trace-picker"
              />
            }
          />
        }
      >
        <div className="btr" style={{ ...vars, backgroundColor: 'transparent' }} data-testid="batch-traceability">
          {refusal ? (
            <RefusalState refusal={refusal} what="Batch Traceability" />
          ) : !selected ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Select a batch" />
          ) : loading ? (
            <div className="btr-card btr-card-body" aria-busy="true">
              <Skeleton active />
            </div>
          ) : error ? (
            <StatusNotice state="error" title={error.status === 404 ? error.message || 'Batch not found' : 'The audit trail could not be loaded'}>
              {error.status === 404 ? null : (
                <Button className="btr-btn" onClick={load} style={{ marginTop: 6 }}>
                  Retry
                </Button>
              )}
            </StatusNotice>
          ) : data ? (
            <>
              <HeroCard data={data} />
              <TrailCard data={data} batchNumber={selected} />
              <MasterRecordCard master={data.master_record} />
            </>
          ) : null}
        </div>
      </Card>
    </div>
  );
};

export default Batch360;
